import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronRight, Radio, X } from "lucide-react";
import { useMonitorSummary, useEvents } from "../../lib/queries";
import { useDateRange } from "../../lib/dateRange";
import { useFeed } from "../../components/shell/AppShell";
import SyncFreshnessBadge from "../../components/SyncFreshnessBadge";
import { GlassCard, SeverityCapsule, Pill, Spinner, SectionTag } from "../../components/ui";
import { KpiTile } from "../../components/KpiTile";
import { MediaBadge } from "../../components/MediaIndicator";
import ExportMenu from "../../components/ExportMenu";
import { fmtAUD, fmtNum, timeAgo, EMESRT_COLOR, SEVERITY_COLOR } from "../../lib/format";
import type { EventRow } from "../../types/api";
import AlertProfileChart from "./AlertProfileChart";
import MapView from "../map/MapView";
import ExceptionDetail from "../exception/ExceptionDetail";

interface FeedFilter {
  label: string;
  params: { severity?: string; min_risk?: number; media_type?: string };
}

/** Apply a KPI drill filter to live WS rows so the stream keeps flowing while
 * a drill is active (the HTTP baseline is filtered server-side). */
function matchesFilter(e: EventRow, filter: FeedFilter | null): boolean {
  if (!filter) return true;
  const { severity, min_risk, media_type } = filter.params;
  if (severity && e.severity !== severity) return false;
  if (min_risk != null && e.risk_score < min_risk) return false;
  if (media_type && e.media?.type !== media_type) return false;
  return true;
}

export default function Monitor() {
  const { data: summary, isLoading } = useMonitorSummary();
  const { preset } = useDateRange();
  const [filter, setFilter] = useState<FeedFilter | null>(null);
  const { data: events } = useEvents({ ...filter?.params, limit: 40 });
  const feed = useFeed();
  const [params, setParams] = useSearchParams();
  const selected = params.get("case") ? Number(params.get("case")) : null;

  const open = (id: number) => setParams((p) => { p.set("case", String(id)); return p; });
  const close = () => setParams((p) => { p.delete("case"); return p; });

  const merged = useMemo(() => {
    // Live WS rows must respect the selected window and any drill filter. The
    // upper bound only applies to custom (historical) ranges — for presets the
    // server-resolved end lags the poll by up to 20 s.
    const rangeStart = summary ? new Date(summary.range.start).getTime() : null;
    const rangeEnd = summary ? new Date(summary.range.end).getTime() : null;
    const inWindow = (e: EventRow) => {
      const ts = new Date(e.start_time).getTime();
      if (rangeStart != null && ts < rangeStart) return false;
      if (preset === "custom" && rangeEnd != null && ts > rangeEnd) return false;
      return true;
    };
    const live = feed.live.filter((e) => inWindow(e) && matchesFilter(e, filter));
    const seen = new Set<number>();
    const out: EventRow[] = [];
    for (const e of [...live, ...(events?.results ?? [])]) {
      if (seen.has(e.id)) continue;
      seen.add(e.id);
      out.push(e);
    }
    return out.slice(0, 18);
  }, [feed.live, events, filter, summary, preset]);

  const k = summary?.kpis;
  const drill = (f: FeedFilter | null) => setFilter((cur) => (cur?.label === f?.label ? null : f));

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <SectionTag>MONITOR · FLEET OVERVIEW</SectionTag>
          <h1 className="text-xl font-semibold">Live Operating View</h1>
        </div>
        <div className="flex items-center gap-2">
          <SyncFreshnessBadge />
          <ExportMenu title="Export feed" extraFilters={filter?.params ?? {}} />
          <Link
            to="/triage"
            className="flex items-center gap-1 rounded-lg border border-aurora-cyan/40 bg-aurora-cyan/10 px-3 py-1.5 text-sm text-aurora-cyan transition hover:bg-aurora-cyan/20"
          >
            Live Triage <ChevronRight size={15} />
          </Link>
        </div>
      </div>

      {isLoading || !k ? (
        <GlassCard><Spinner /></GlassCard>
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
          <KpiTile label="ACTIVE ASSETS" value={fmtNum(k.active_assets.value)} accent="var(--aurora-cyan)" />
          <KpiTile label="PERSONNEL" value={fmtNum(k.personnel.value)} />
          <KpiTile label="CRITICAL" value={fmtNum(k.critical.value)} deltaPct={k.critical.delta_pct}
                   spark={k.critical.spark} critical invertDelta
                   onClick={() => drill({ label: "critical", params: { min_risk: 70 } })} />
          <KpiTile label="RISK AVERTED" value={fmtAUD(k.risk_averted_aud.value)} deltaPct={k.risk_averted_aud.delta_pct}
                   accent="var(--aurora-teal)" />
          <KpiTile label="SAFETY SCORE" value={`${k.safety_score.value}%`} deltaPct={k.safety_score.delta_pct}
                   accent="var(--pacific)" />
          <KpiTile label="EXCEPTIONS" value={fmtNum(k.events.value)} deltaPct={k.events.delta_pct}
                   spark={k.events.spark} accent="var(--accent)" invertDelta
                   onClick={() => drill({ label: "all", params: {} })} />
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <GlassCard>
          <SectionTag>ALERT PROFILE</SectionTag>
          <AlertProfileChart />
        </GlassCard>
        <GlassCard className="min-h-[300px] overflow-hidden bg-ocean-900/40 p-0">
          <MapView variant="embedded" />
        </GlassCard>
      </div>

      <GlassCard>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SectionTag>REAL-TIME EXCEPTION FEED</SectionTag>
            {filter && (
              <Pill color="#22D3EE" className="!cursor-pointer">
                <button onClick={() => setFilter(null)} className="flex items-center gap-1">
                  {filter.label} <X size={10} />
                </button>
              </Pill>
            )}
          </div>
          <span className="flex items-center gap-1.5 text-[11px] text-slatey">
            <Radio size={13} className={feed.connected ? "text-aurora-teal" : "text-slate-600"} />
            {feed.connected ? "streaming" : "reconnecting…"}
          </span>
        </div>
        <div className="max-h-[440px] space-y-1.5 overflow-y-auto pr-1">
          {merged.map((e) => (
            <FeedRow key={`${e.id}-${e.start_time}`} e={e} onClick={() => open(e.id)} />
          ))}
          {merged.length === 0 && <div className="py-8 text-center text-sm text-slatey">No exceptions in range.</div>}
        </div>
      </GlassCard>

      <div className="grid gap-3 lg:grid-cols-3">
        <GlassCard>
          <SectionTag>ASSET MIX · BY TAXONOMY</SectionTag>
          <div className="space-y-2">
            {(summary?.asset_mix ?? []).map((a) => {
              const max = summary!.asset_mix[0]?.count || 1;
              return (
                <div key={a.machine_type} className="flex items-center gap-2 text-xs">
                  <span className="w-28 truncate text-slatey">{a.machine_type || "Unspecified"}</span>
                  <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-white/5">
                    <div className="h-full rounded-full bg-gradient-to-r from-pacific to-aurora-cyan"
                         style={{ width: `${(a.count / max) * 100}%` }} />
                  </div>
                  <span className="mono w-6 text-right">{a.count}</span>
                </div>
              );
            })}
          </div>
        </GlassCard>

        <GlassCard>
          <SectionTag>LIQUID AI MODELS · STATUS</SectionTag>
          <div className="space-y-2">
            {(summary?.models ?? []).map((m) => (
              <div key={`${m.name}-${m.version}`} className="flex items-center justify-between text-xs">
                <div>
                  <div className="font-medium">{m.name} <span className="mono text-slatey">v{m.version}</span></div>
                  <div className="text-[10px] text-slatey">
                    acc {(m.accuracy * 100).toFixed(1)}% · drift {m.drift_30d}% · {m.latency_ms}ms
                  </div>
                </div>
                <Pill color={m.status === "prod" ? "#2DD4BF" : m.status === "canary" ? "#F59E0B" : "#64748B"}>
                  {m.status}
                </Pill>
              </div>
            ))}
          </div>
        </GlassCard>

        <GlassCard>
          <SectionTag>INTERVENTIONS · BY EMESRT</SectionTag>
          <div className="space-y-3 pt-1">
            {(summary?.interventions_by_layer ?? []).map((l) => {
              const max = Math.max(...(summary?.interventions_by_layer ?? [{ count: 1 }]).map((x) => x.count));
              return (
                <div key={l.emesrt_level} className="flex items-center gap-2 text-xs">
                  <span className="mono w-8 rounded px-1 text-center"
                        style={{ color: EMESRT_COLOR[l.emesrt_level], background: `${EMESRT_COLOR[l.emesrt_level]}1a` }}>
                    {l.emesrt_level}
                  </span>
                  <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-white/5">
                    <div className="h-full rounded-full" style={{ width: `${(l.count / max) * 100}%`, background: EMESRT_COLOR[l.emesrt_level] }} />
                  </div>
                  <span className="mono w-8 text-right">{l.count}</span>
                </div>
              );
            })}
            {(summary?.interventions_by_layer ?? []).length === 0 && (
              <div className="text-xs text-slatey">No interventions in window.</div>
            )}
          </div>
        </GlassCard>
      </div>

      <ExceptionDetail eventId={selected} onClose={close} />
    </div>
  );
}

function FeedRow({ e, onClick }: { e: EventRow; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-lg border border-white/5 bg-white/[0.03] px-3 py-2 text-left text-xs transition hover:border-aurora-cyan/30 hover:bg-white/5 edge-${e.severity}`}
    >
      <span className="mono w-16 shrink-0 text-slatey">{timeAgo(e.start_time)}</span>
      <span className="w-44 shrink-0 truncate font-medium">{e.event_type_label}</span>
      <span className="w-20 shrink-0 truncate text-slatey">{e.asset_registration ?? "—"}</span>
      <span className="hidden w-24 shrink-0 truncate text-slatey md:block">{e.site_title ?? "—"}</span>
      <MediaBadge media={e.media} />
      <span className="mono ml-auto shrink-0" style={{ color: SEVERITY_COLOR[e.severity] }}>
        {Math.round(e.risk_score)}%
      </span>
      <SeverityCapsule severity={e.severity} />
      <span className="hidden shrink-0 items-center gap-0.5 text-aurora-cyan sm:flex">Review <ChevronRight size={13} /></span>
    </button>
  );
}
